import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Component({
  selector: 'login-page',
  templateUrl: './login.component.html',
  styleUrls: [],
})
export class LoginComponent implements OnInit {
  hide = true;

  public loginForm!: FormGroup;
  public profileForm!: FormGroup;
  public showPersonalDataForm = false;
  public profileUpdating = false;
  public user: any;
  public isRedirectLoginInProgress = false;
  public readonly maxBirthDate = moment().format('YYYY-MM-DD');
  public readonly namePattern = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ .-]+$/;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private snackBar: MatSnackBar
  ) {}
  
  async ngOnInit(): Promise<void> {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
    this.profileForm = this.fb.group({
      firstName: [
        '',
        [Validators.required, Validators.maxLength(20), Validators.pattern(this.namePattern)],
      ],
      lastName: [
        '',
        [Validators.required, Validators.maxLength(25), Validators.pattern(this.namePattern)],
      ],
      gender: ['', Validators.required],
      birthday: ['', Validators.required],
    });

    const { email, idToken } = this.activatedRoute.snapshot.queryParams;

    if (email) {
      this.loginForm.patchValue({ username: decodeURIComponent(email) });
    }

    if (email && idToken) return this.handlePlatformRedirectLogin(decodeURIComponent(email), idToken);

    let connected = await this.authService.isConnected();
    if (connected) {
      const authUser = JSON.parse(localStorage.getItem('user') || 'null');
      if (authUser?.uid) {
        const user = await this.firebaseService.getDocument(`/users/${authUser.uid}`);
        if (user && !(user.firstName && user.lastName && user.gender && user.birthday)) {
          return this.startCompleteProfileFlow({ ...user, uid: authUser.uid });
        }
      }

      let userInfo = localStorage.getItem('userInfo')!;
      let url: string = `/admin-users-table`;
      this.router.navigateByUrl(url);
    }
  }

  private handlePlatformRedirectLogin = async (email: string, idToken: string): Promise<void> => {
    if (this.isRedirectLoginInProgress) return;

    this.isRedirectLoginInProgress = this.profileUpdating = true;

    try {
      await this.authService.logout();
      this.proceed(await this.authService.loginWithApiToken(email, idToken));
    } catch (error) {
      console.error('Platform redirect login failed', error);
      this.handleLoginError('auth/token-login-failed');
      await this.authService.logout();
    } finally {
      await this.clearPlatformRedirectParams();
      this.isRedirectLoginInProgress = this.profileUpdating = false;
    }
  };

  private clearPlatformRedirectParams = (): Promise<boolean> => {
    return this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        email: null,
        idToken: null,
        source: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  };

  isValid() {
    return (
      this.loginForm.controls['username'].status === 'VALID' &&
      this.loginForm.controls['password'].status === 'VALID'
    );
  }

  navToPlatform() {
    window.open('https://simskills.io/#demo', '_blank');
  }

  login() {
    if (
      !(
        this.loginForm.controls['username'].value &&
        this.loginForm.controls['password'].value &&
        this.isValid()
      )
    ) {
      return;
    }

    let data = {
      email: this.loginForm.controls['username'].value,
      password: this.loginForm.controls['password'].value,
    };
    this.authService
      .emailAuth(data)
      .then((user) => {
        if (user) {
          // Check if the email is verified?
          this.proceed(user);
        }
      })
      .catch((error) => {
        this.handleLoginError(error.code);
      });
  }

  handleLoginError(error: string) {
    let errorMessage: string;
    switch (error) {
      case 'auth/user-not-found':
        errorMessage = this.translate.instant('LOGIN_ERRORS.USER_NOT_FOUND');
        break;
      case 'auth/invalid-email':
        errorMessage = this.translate.instant('LOGIN_ERRORS.INVALID_EMAIL');
        break;
      case 'auth/wrong-password':
        errorMessage = this.translate.instant('LOGIN_ERRORS.WRONG_PASSWORD');
        break;
      case 'auth/email-already-in-use':
        errorMessage = this.translate.instant(
          'LOGIN_ERRORS.EMAIL_ALREADY_IN_USE'
        );
        break;
      default:
        errorMessage = this.translate.instant('LOGIN_ERRORS.DEFAULT_ERROR');
        break;
    }

    this.snackBar.open(errorMessage, '', {
      duration: 4000,
    });
  }

  proceed(user: {
    [x: string]: any;
    approved?: any;
    uid?: any;
    company?: any;
    companyLowercase?: any;
    companyId?: any;
    firstName?: any;
    lastName?: any;
    displayName?: any;
    role?: string;
  }) {
    // Check if user already exists
    if (user.approved) {
      this.firebaseService.getDocument(`/users/${user.uid}`).then((res: any) => {
        // Exisiting user
        if (res) {
          res.uid = user.uid;
          if (!(res.firstName && res.lastName && res.gender && res.birthday)) {
            return this.startCompleteProfileFlow(res);
          }

          let afterLoginUrl: string = '';
          // Save userData
          const userInfo = JSON.stringify(res);
          localStorage.setItem('userInfo', userInfo);
          let parsedUser = JSON.parse(userInfo);
          if (!parsedUser.role) {
            let errorMessage: string = this.translate.instant(
              'LOGIN_ERRORS.PERMISSION_DENIED'
            );
            this.snackBar.open(errorMessage, '', {
              duration: 4000,
            });
            this.authService.logout();
          } else {
            // set las login.
            this.firebaseService.set(`/user_journey/${res.company}/${user.uid}/last_login`,  moment().format()).then();
            if (parsedUser.role === 'manager') {
              afterLoginUrl = '/manager-users-table';
            }
            if (parsedUser.role === 'admin') {
              afterLoginUrl = '/admin-users-table';
            }

            this.router.navigateByUrl(afterLoginUrl).then(() => {
              window.location.reload();
            });
          }
        }
        // New user
        else {
          console.log('user doesnt exist');
        }
      });
    } else {
      this.authService.logout();
    }
  }

  public updatePersonalData = async () => {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.profileUpdating = true;

    const updatedData = {
      firstName: this.normalizeName(this.profileForm.controls['firstName'].value),
      lastName: this.normalizeName(this.profileForm.controls['lastName'].value),
      gender: this.profileForm.controls['gender'].value,
      birthday: moment(this.profileForm.controls['birthday'].value).format('YYYY-MM-DD'),
    };
    updatedData['displayName'] = `${updatedData.firstName} ${updatedData.lastName}`.trim();

    if (this.user.company && !this.user.companyLowercase) {
      updatedData['companyLowercase'] = this.user.company.toLowerCase().replace(/ /g, '-');
    }

    try {
      await this.firebaseService.updateDocument('/users', this.user.uid, updatedData);
      await this.firebaseService.updateDocument(`/user_journey/${this.user.companyLowercase}`, this.user.uid, {
        name: updatedData['displayName']
      });
      Object.assign(this.user, updatedData);
      localStorage.setItem('userInfo', JSON.stringify(this.user));

      let afterLoginUrl: string = '';
      if (this.user.role === 'manager') {
        afterLoginUrl = '/manager-users-table';
      }
      if (this.user.role === 'admin') {
        afterLoginUrl = '/admin-users-table';
      }

      this.showPersonalDataForm = false;
      this.router.navigateByUrl(afterLoginUrl).then(() => {
        window.location.reload();
      });
    } catch (error) {
      this.handleLoginError('default');
    } finally {
      this.profileUpdating = false;
    }
  }

  private startCompleteProfileFlow = (user: any): void => {
    this.user = user;
    localStorage.setItem('userInfo', JSON.stringify(user));
    this.showPersonalDataForm = true;
    this.profileForm.patchValue({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      gender: user.gender || '',
      birthday: user.birthday ? moment(user.birthday, 'YYYY-MM-DD', true).toDate() : null,
    });
  }

  private normalizeName = (value: string): string => {
    return value.trim().replace(/\s+/g, ' ');
  }
}
